<template>
  <b-card bg-variant="light">
    <b-form-group
      class="mb-0"
      label-cols-lg="3"
      label="Personal Information"
      label-size="lg"
      label-class="font-weight-bold pt-0"
    >
      <b-form-row>
        <b-col cols="12" md="6">
          <b-form-group label="First Name:" label-for="first-name">
            <b-form-input
              id="first-name"
              style="text-transform: capitalize"
              v-model="v.firstname.$model"
              @blur="$emit('fill-account-name')"
            >
            </b-form-input>
            <span
              v-if="!v.firstname.required && v.firstname.$dirty"
              class="text-danger"
              >First Name is required!
            </span>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Last Name:" label-for="last-name">
            <b-form-input
              id="last-name"
              style="text-transform: capitalize"
              v-model="v.lastname.$model"
              @blur="$emit('fill-account-name')"
            >
            </b-form-input>

            <span
              v-if="!v.lastname.required && v.lastname.$dirty"
              class="text-danger"
              >Last Name is required!
            </span>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-group label="Account Name:" label-for="account-name">
        <b-form-input id="account-name" v-model="accountNameV.$model">
        </b-form-input>
        <!-- $v.account.name is `required` and gates the whole submission, but
             was the one field in this card with no message: clearing it refused
             the renewal with nothing on screen to explain why. -->
        <span
          v-if="!accountNameV.required && accountNameV.$dirty"
          class="text-danger"
          >Account Name is required!
        </span>
      </b-form-group>

      <b-form-group label="Email:" label-for="email">
        <b-form-input id="email" v-model="v.email.$model"></b-form-input>
        <span v-if="!v.email.required && v.email.$dirty" class="text-danger"
          >Email is required!
        </span>

        <span
          v-if="!v.email.email && v.email.$dirty && v.email.required"
          class="text-danger"
          >Email must be a valid email address (include @ and domain)
        </span>
      </b-form-group>

      <b-form-row>
        <b-col cols="12" md="6">
          <b-form-group label="Primary Phone:" label-for="primary-phone">
            <b-form-input
              id="primary-phone"
              v-model="v.phone.$model"
              @blur="normalizePhoneField('phone')"
            ></b-form-input>
            <span v-if="!v.phone.required && v.phone.$dirty" class="text-danger"
              >Primary Phone is required!
            </span>

            <span v-if="!v.phone.integer && v.phone.$dirty" class="text-danger"
              >Phone numbers should consist of only numbers. ex: 6315664283
            </span>

            <span
              v-if="v.phone.integer && !v.phone.minlength && v.phone.$dirty"
              class="text-danger"
              >Phone numbers must be at least 10 digits long.
            </span>

            <!-- maxLength had no message at all, so an eleven digit number
                 failed validation in silence: the submit was refused with
                 "Something isn't right with the form" and every box looked
                 fine. -->
            <span
              v-if="v.phone.integer && !v.phone.maxLength && v.phone.$dirty"
              class="text-danger"
              >Phone numbers must be no more than 10 digits long. Drop the
              country code or extension.
            </span>

            <!-- phoneAdvisory returns null when the number is not ten digits,
                   so without this guard the span rendered empty and the field
                   showed a validation failure with no text. -->
            <span
              v-if="!v.phone.nanp && v.phone.$dirty && phoneProblem"
              class="text-danger"
              >{{ phoneProblem }}
            </span>

            <small v-else-if="phoneProblem" class="text-warning d-block"
              >{{ phoneProblem }} &mdash; already on file, so it will not block
              this renewal.
            </small>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Secondary Phone:" label-for="secondary-phone">
            <b-form-input
              id="secondary-phone"
              v-model="v.mobilephone.$model"
              @blur="normalizePhoneField('mobilephone')"
            ></b-form-input>
            <span
              v-if="!v.mobilephone.integer && v.mobilephone.$dirty"
              class="text-danger"
              >Phone numbers should consist of only numbers. ex: 6315664283
            </span>

            <span
              v-if="
                v.mobilephone.integer &&
                !v.mobilephone.minlength &&
                v.mobilephone.$dirty
              "
              class="text-danger"
              >Phone numbers must be at least 10 digits long.
            </span>

            <span
              v-if="
                v.mobilephone.integer &&
                !v.mobilephone.maxLength &&
                v.mobilephone.$dirty
              "
              class="text-danger"
              >Phone numbers must be no more than 10 digits long. Drop the
              country code or extension.
            </span>

            <span
              v-if="
                !v.mobilephone.nanp && v.mobilephone.$dirty && mobileProblem
              "
              class="text-danger"
              >{{ mobileProblem }}
            </span>

            <small v-else-if="mobileProblem" class="text-warning d-block"
              >{{ mobileProblem }} &mdash; already on file, so it will not block
              this renewal.
            </small>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-button
        v-if="isRenew"
        class="update-button"
        :disabled="!this.isRenew && this.accountSfid !== undefined"
        @click="$emit('update-contact')"
        type="button"
        variant="primary"
        >Update Account Information</b-button
      >
    </b-form-group>
  </b-card>
</template>

<script>
// Personal Information card. Contact fields bind through the `v` prop (parent's
// $v.contacts subtree); the account name binds through `accountNameV`
// ($v.account.name). $model setters write back to parent data, so behavior is
// unchanged. FillAccountName / updateContactInfo are emitted to the parent.
import { normalizePhone, phoneAdvisory } from "./contactValidation";

export default {
  name: "ContactCard",
  props: {
    v: { type: Object, required: true },
    accountNameV: { type: Object, required: true },
    isRenew: { type: Boolean, default: false },
    accountSfid: { type: String, default: undefined },
  },
  computed: {
    // What is wrong with the number, if anything. Rendered as an error when
    // the dealer typed it and as a warning when the record arrived with it -
    // the parent's `nanp` rule draws that line, and blocking a renewal over a
    // number the member has had for years is not worth the correction.
    phoneProblem() {
      return phoneAdvisory(this.v.phone.$model);
    },
    mobileProblem() {
      return phoneAdvisory(this.v.mobilephone.$model);
    },
  },
  methods: {
    // Formatting a dealer typed for readability is not a mistake to report, so
    // repair it on the way out of the field. Only an unambiguous result is
    // written back: anything that does not resolve to ten digits is left as
    // typed, so the existing digits/length messages still explain themselves
    // rather than the box silently emptying.
    normalizePhoneField(field) {
      const current = this.v[field].$model;
      if (current === null || current === undefined || current === "") return;

      const digits = normalizePhone(current);
      if (digits.length === 10 && digits !== current) {
        this.v[field].$model = digits;
      }
    },
  },
};
</script>
